import { API_ENDPOINT } from '../constant/constant.ts'
import type { useLoginStore as useLoginStoreType } from '@/store/LoginStore.ts'

// 토큰 갱신 중인지 확인하는 플래그 및 대기 큐
let isRefreshing = false
let failedQueue: Array<{
    resolve: (value?: any) => void
    reject: (reason?: any) => void
}> = []

function flushFailedQueue(error: any = null) {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error)
        } else {
            prom.resolve()
        }
    })
    failedQueue = []
}

export interface ApiRes<T> {
    data: T
    code?: string | number
    message?: string
}

export class ApiError extends Error {
    readonly status: number
    readonly serverMessage?: string

    constructor(message: string, status: number, serverMessage?: string) {
        super(message)
        this.name = 'ApiError'
        this.status = status
        this.serverMessage = serverMessage
    }
}

// 공통 UI에서 사용하는 이전 이름과 호환한다. 두 이름은 같은 오류 타입이다.
export { ApiError as ApiRequestError }

/** 401 뒤 토큰 갱신이 일시적으로 안 돼(429·5xx·네트워크) 요청을 마치지 못했을 때의 오류 메시지 */
export const REFRESH_UNAVAILABLE_MESSAGE = 'REFRESH_UNAVAILABLE'

const USER_FACING_ERROR_STATUSES = [400, 404, 409, 422]

export const getApiErrorMessage = (error: unknown, fallback: string): string => {
    if (
        error instanceof ApiError &&
        error.serverMessage &&
        USER_FACING_ERROR_STATUSES.includes(error.status)
    ) {
        return error.serverMessage
    }
    return fallback
}

const extractServerMessage = (body: any): string | undefined => {
    const message = body?.message
    if (typeof message === 'string' && message.trim()) return message
    if (Array.isArray(message)) {
        const joined = message.filter((item) => typeof item === 'string').join('\n')
        if (joined) return joined
    }
    return undefined
}

export const createRequestConfig = (
    options: RequestInit = {},
    accessToken = ''
): RequestInit => {
    const headers = new Headers(options.headers)
    const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData

    if (!headers.has('Accept')) headers.set('Accept', 'application/json')
    if (!isFormData && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json')
    }
    if (accessToken && !headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${accessToken}`)
    }

    return {
        ...options,
        headers,
        credentials: 'include'
    }
}

/**
 * 모든 API 요청의 베이스가 되는 인터셉터 함수
 */
const baseRequest = async <T>(
    url: string,
    options: RequestInit = {},
    isMocking: boolean = false
): Promise<ApiRes<T>> => {
    const endpoint = isMocking ? url : `${API_ENDPOINT}${url}`
    const { useLoginStore } = (await import('@/store/LoginStore.ts')) as {
        useLoginStore: typeof useLoginStoreType
    }

    const accessToken = useLoginStore.getState().accessToken
    const config = createRequestConfig(options, accessToken)

    let response = await fetch(endpoint, config)

        // 401 Unauthorized 처리 (로그인/새로고침/로그아웃 API 제외)
        if (
            response.status === 401 &&
            !url.includes('/auth/login') &&
            !url.includes('/auth/refresh') &&
            !url.includes('/auth/logout')
        ) {
            if (isRefreshing) {
                // 이미 다른 요청이 갱신 중이면 큐에서 대기
                await new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject })
                })
                // 갱신 완료 후 재시도 (최신 토큰 주입)
                const newAccessToken = useLoginStore.getState().accessToken
                if (newAccessToken) {
                    const headers = new Headers(config.headers)
                    headers.set('Authorization', `Bearer ${newAccessToken}`)
                    config.headers = headers
                }
                response = await fetch(endpoint, config)
            } else {
                isRefreshing = true
                try {
                    // Silent Refresh 시도
                    const { apiRefreshOutcome } = await import('@/api/auth/refresh.ts')
                    const outcome = await apiRefreshOutcome()

                    if (outcome.status === 'rejected') {
                        // 서버가 갱신을 거절했다. 세션이 끝났으므로 로그아웃한다.
                        const error = new Error('Refresh Failed')
                        flushFailedQueue(error)
                        await useLoginStore.getState().logout()
                        window.location.href = '/login'
                        throw error
                    }

                    if (outcome.status === 'unavailable') {
                        // 429·5xx·네트워크 오류. 세션은 그대로 두고 이 요청만 실패시킨다.
                        const error = new ApiError(REFRESH_UNAVAILABLE_MESSAGE, 401)
                        flushFailedQueue(error)
                        throw error
                    }

                    // Zustand State 업데이트
                    await useLoginStore.getState().login('', outcome.token)

                    // 대기 중인 요청들 해제
                    flushFailedQueue(null)

                    // 현재 요청 재시도
                    const headers = new Headers(config.headers)
                    headers.set('Authorization', `Bearer ${outcome.token}`)
                    config.headers = headers
                    response = await fetch(endpoint, config)
                } finally {
                    // 예기치 못한 오류로 큐를 비우지 못한 채 끝나도 대기 중인 요청이 멈춰 있지 않게 한다.
                    if (failedQueue.length > 0) {
                        flushFailedQueue(new ApiError(REFRESH_UNAVAILABLE_MESSAGE, 401))
                    }
                    isRefreshing = false
                }
            }
        }

        if (!response.ok) {
            let serverMessage: string | undefined
            try {
                const errorBody = await response.clone().json()
                console.error(
                    `[API Error] ${options.method || 'GET'} ${url} ${response.status}:`,
                    errorBody
                )
                serverMessage = extractServerMessage(errorBody)
            } catch {
                console.error(
                    `[API Error] ${options.method || 'GET'} ${url} ${response.status}: ${response.statusText}`
                )
            }
            throw new ApiError(
                serverMessage || `HTTP ${response.status}: ${response.statusText}`,
                response.status,
                serverMessage
            )
        }

        // 응답 컨텐츠 타입 확인
        const contentType = response.headers.get('content-type') || ''
        const isBinaryType =
            contentType.includes('application/vnd.openxmlformats-officedocument') || // Modern Excel, Word, etc.
            contentType.includes('application/vnd.ms-excel') || // Old Excel
            contentType.includes('application/pdf') ||
            contentType.includes('image/') ||
            contentType.includes('application/octet-stream')

        if (isBinaryType) {
            // 명확한 바이너리 파일인 경우 (예: 엑셀 Blob)
            const blob = await response.blob()
            return { data: blob as unknown as T, code: 200, message: 'success' }
        }

        const responseText = await response.text()
        if (!responseText) {
            return { data: undefined as T, code: response.status, message: 'success' }
        }

        let res: any
        try {
            res = JSON.parse(responseText)
        } catch {
            return { data: responseText as T, code: response.status, message: 'success' }
        }

        // 백엔드 응답 규격 처리 (표준화)
    if (Array.isArray(res)) {
        return { data: res as T, code: 200, message: 'success' }
    } else if (res.data !== undefined) {
        return { data: res.data, code: res.code, message: res.message }
    } else {
        return { data: res as T, code: 200, message: 'success' }
    }
}

export const apiDownload = async (url: string): Promise<Blob> => {
    const res = await baseRequest<Blob>(url, { method: 'GET' })
    return res.data
}

export const apiGet = async <T>(url: string, isMocking: boolean = false): Promise<ApiRes<T>> => {
    return baseRequest<T>(url, { method: 'GET' }, isMocking)
}

export const apiPost = async <T>(
    url: string,
    body?: any,
    isMocking: boolean = false
): Promise<ApiRes<T>> => {
    return baseRequest<T>(
        url,
        {
            method: 'POST',
            body: body !== undefined ? JSON.stringify(body) : undefined
        },
        isMocking
    )
}

export const apiPatch = async <T>(url: string, body?: any): Promise<ApiRes<T>> => {
    return baseRequest<T>(url, {
        method: 'PATCH',
        body: body !== undefined ? JSON.stringify(body) : undefined
    })
}

export const apiDelete = async <T>(url: string, body?: any): Promise<ApiRes<T>> => {
    return baseRequest<T>(url, {
        method: 'DELETE',
        body: body !== undefined ? JSON.stringify(body) : undefined
    })
}

export const apiPostFormData = async <T>(url: string, body?: any): Promise<ApiRes<T>> => {
    const formData = new FormData()
    if (body) {
        Object.keys(body).forEach((key) => {
            const value = body[key]
            if (value !== undefined && value !== null) formData.append(key, value)
        })
    }

    return baseRequest<T>(url, {
        method: 'POST',
        headers: {
            Accept: 'application/json'
        },
        body: formData
    })
}

export const apiPatchFormData = async <T>(url: string, body?: any): Promise<ApiRes<T>> => {
    const formData = new FormData()
    if (body) {
        Object.keys(body).forEach((key) => {
            const value = body[key]
            if (value !== undefined && value !== null) formData.append(key, value)
        })
    }

    return baseRequest<T>(url, {
        method: 'PATCH',
        headers: { Accept: 'application/json' },
        body: formData
    })
}
